import {
  DocumentationTabLegalEntityQuery,
  Category as gqlCategory,
  Eligibility as gqlEligibility,
  EntityType as gqlEntityType,
  TaxStatus
} from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { UPDATE_LEGAL_ENTITY_MUTATION } from '@/components/investors-page/editInvestorPage/queries';
import { EditInvestorEntityDetailsTaxStatusOptionEnum } from '@/components/investors-page/editInvestorPage/types';
import { IInvestorDetailsStepForm, InvestorDetailsStep } from '@/components/transactions-page/generateSignaturePacket-page/steps/investorDetails';
import { useErrorMessages } from '@/hooks/i18n';
import { getEnumKeyByValue } from '@/shared/utils/enums';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { EntityType } from '@trace-8/trace8-schema';
import { useState } from 'react';

interface EditInvestorDetailsModalPageProps {
  investorDetailsData: IInvestorDetailsStepForm & { id: string };
  documentationTabDataRefetch: () => Promise<ApolloQueryResult<DocumentationTabLegalEntityQuery>>;
  handleCloseEditPage: () => void;
}

export const EditInvestorDetailsModalPage = ({
  investorDetailsData,
  documentationTabDataRefetch,
  handleCloseEditPage
}: EditInvestorDetailsModalPageProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [updateEntity, { loading: updateEntityLoading }] = useMutation(UPDATE_LEGAL_ENTITY_MUTATION, {
    onError: (error) => {
      console.error(error);
      window.scrollTo(0, 0);
      setErrorMessage(defaultErrorMessage);
    }
  });

  const saveInvestorDetailsData = async (data: IInvestorDetailsStepForm) => {
    if (!investorDetailsData.id) {
      setErrorMessage(defaultErrorMessage);
      return;
    }
    const { entityType, category, eligibility, taxStatus, otherType } = data;
    const taxStatusEnumKey: TaxStatus = getEnumKeyByValue(EditInvestorEntityDetailsTaxStatusOptionEnum, taxStatus) as TaxStatus;

    await updateEntity({
      variables: {
        data: {
          id: investorDetailsData.id,
          taxStatus: taxStatusEnumKey,
          category: category as gqlCategory,
          eligibility: eligibility as gqlEligibility[],
          entityType: entityType as gqlEntityType,
          otherType: entityType === getEnumKeyByValue(EntityType, EntityType.Other) ? otherType : null
        }
      }
    });

    await documentationTabDataRefetch();
    handleCloseEditPage();
  };

  return (
    <>
      {updateEntityLoading && (
        <ModalWrappedContent isOpen={updateEntityLoading} title="Saving" showCancelButton={false} showCloseIcon={false} onClose={() => {}}>
          <ModalLoader />
        </ModalWrappedContent>
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleCloseEditPage} />} />
        <PageTitle title="Investor Details" />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <PaddingWrap>
          <InvestorDetailsStep
            mode="documentationTab"
            investorDetailsStepData={investorDetailsData}
            saveInvestorDetailsData={saveInvestorDetailsData}
            onCancel={handleCloseEditPage}
          />
        </PaddingWrap>
      </MainWrap>
    </>
  );
};
